import {z} from 'zod'

import {BlockHighlightVisualizationEnum, CreateOrUpdateBlockHighlightDocument} from '@sdox/api'

import {EditorFeature} from '../../../../inputs/TipTap/types'
import {highlightSchema} from '../../utils/schema'
import {createFormConfig} from '../../utils/createFormConfig'
import {updateMetaSchema} from '../../utils/getUpdateMetaFromFieldValues'
import {buttonFormToInput, buttonFragmentToForm} from '../../utils/buttonMappers'
import {MediaLibraryFileType} from '../../../../MediaLibrary/types'

import type {
  BlockButtonInput,
  BlockHighlightInput,
  CreateOrUpdateBlockHighlightMutation,
  XcNwBlock_BlockHighlightType_Fragment,
} from '@sdox/api'

type UpdateTextImageValidatorSchema = z.infer<typeof updateTextImageValidatorSchema>

const updateTextImageValidatorSchema = z.object({
  data: highlightSchema,
  meta: updateMetaSchema,
})

export const textImageConfig = createFormConfig<
  UpdateTextImageValidatorSchema,
  CreateOrUpdateBlockHighlightMutation,
  BlockHighlightInput,
  XcNwBlock_BlockHighlightType_Fragment
>({
  mutationOptions: {
    mutation: CreateOrUpdateBlockHighlightDocument,
  },
  fieldMapper: ({data}) => ({
    title: data.title,
    description: data.description,
    fileHash: data.image.fileHash,
    visualization: data.visualization,
    cta: buttonFormToInput(data.cta) as BlockButtonInput,
  }),
  validatorSchema: updateTextImageValidatorSchema,
  inputFields: [
    {
      label: 'Ausrichtung',
      fieldName: 'data.visualization',
      inputType: 'select',
      options: [
        {label: 'Erst Bild, dann Text', value: BlockHighlightVisualizationEnum.TextImage},
        {label: 'Erst Text, dann Bild', value: BlockHighlightVisualizationEnum.TextImageViceVersa},
      ],
      defaultValue: BlockHighlightVisualizationEnum.Default,
      required: false,
    },
    {
      label: 'Bild oder Video',
      fieldName: 'data.image',
      inputType: 'file',
      fileTypes: [
        MediaLibraryFileType.Image,
        MediaLibraryFileType.Video,
      ],
      required: true,
      aspectRatio: '3/2',
    },
    {
      label: 'Title',
      fieldName: 'data.title',
      inputType: 'richtext',
      editorConfig: {
        spanAsParagraph: true,
        maxCharCount: 250,
        features: [
          EditorFeature.paragraph,
          EditorFeature.footnote,
          EditorFeature.nobreak,
          EditorFeature.linebreak,
        ],
      },
      required: true,
    },
    {
      label: 'Description',
      fieldName: 'data.description',
      inputType: 'textarea',
      defaultValue: '',
      required: true,
    },
    {
      label: 'Button Config',
      fieldName: 'divider',
      inputType: 'sectionDivider',
      defaultValue: '',
    },
    {
      label: 'Button',
      fieldName: 'data.cta',
      inputType: 'button',
      defaultValue: '',
      required: true,
    },
  ],
  getDefaultValues: (block) => {
    if (!block) return {meta: {}, data: {}}

    const data = {
      title: block.highlightTitle,
      description: block.highlightDescription,
      visualization: block.visualization,
      image: block.media,
      cta: buttonFragmentToForm(block.cta),
    }

    return {
      data,
      meta: {
        pullout: block.pullout,
        countryCodes: block.countryCodes,
      },
    }
  },
})
